import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Calendar, AlertCircle, BarChart3, TrendingUp, User, Euro, Coins } from "lucide-react";
import { invokeFunctions } from "@/api/integrations";
import { Skeleton } from "@/components/ui/skeleton";

export default function Metrics() {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['crm-metrics'],
    queryFn: async () => {
      const response = await invokeFunctions('getMetrics', {});
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Métricas del CRM</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-[400px] w-full rounded-xl" />
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-50 dark:bg-red-900/20 p-8 rounded-2xl inline-block">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-700 dark:text-red-400">Error al cargar métricas</h2>
          <p className="text-red-600 dark:text-red-300 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Métricas del Negocio</h1>
          <p className="text-gray-500 dark:text-gray-400">Resumen detallado de la actividad comercial</p>
        </div>
        <div className="bg-indigo-50 dark:bg-indigo-900/30 px-4 py-2 rounded-lg text-sm text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Actualizado: {new Date(metrics.timestamp).toLocaleTimeString()}
        </div>
      </div>

      {/* Previsiones de Ingresos Section */}
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-8 mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-fuchsia-500" />
        Previsión de Ingresos (Forecast)
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <MetricCard 
          title="Forecast Total" 
          value={`${(metrics.forecastTotal || 0).toLocaleString('es-ES')}€`} 
          icon={Euro} 
          color="fuchsia" 
          description="Suma de valor esperado de todos los leads activos"
        />
        <MetricCard 
          title="Previsión de este Mes" 
          value={`${(metrics.forecastMes || 0).toLocaleString('es-ES')}€`} 
          icon={Coins} 
          color="blue" 
          description="Ingresos potenciales de leads creados este mes"
        />
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard 
          title="Total Leads" 
          value={metrics.totalLeads} 
          icon={Users} 
          color="indigo" 
          description="Cartera total de contactos"
        />
        <MetricCard 
          title="Leads de Hoy" 
          value={metrics.leadsHoy} 
          icon={TrendingUp} 
          color="green" 
          description="Nuevas entradas hoy"
        />
        <MetricCard 
          title="Alta Prioridad" 
          value={metrics.altaPrioridad} 
          icon={AlertCircle} 
          color="red" 
          highlight 
          description="Requieren atención inmediata"
        />
        <MetricCard 
          title="Tasa Actividad" 
          value={`${((metrics.leadsHoy / (metrics.totalLeads || 1)) * 100).toFixed(1)}%`} 
          icon={BarChart3} 
          color="amber" 
          description="Crecimiento vs histórico"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Status */}
        <Card className="border-none shadow-sm bg-white dark:bg-gray-900 overflow-hidden">
          <CardHeader className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-500" />
              Distribución por Estado
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Object.entries(metrics.porEstado).map(([estado, count]) => (
                <div key={estado} className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize text-gray-600 dark:text-gray-300">{estado.replace(/_/g, ' ')}</span>
                    <span className="font-bold text-gray-900 dark:text-white">{count}</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-indigo-500 h-full rounded-full transition-all duration-500" 
                      style={{ width: `${(count / metrics.totalLeads) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By User */}
        <Card className="border-none shadow-sm bg-white dark:bg-gray-900 overflow-hidden">
          <CardHeader className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-500" />
              Leads por Propietario
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Object.entries(metrics.porUsuario).sort((a, b) => b[1] - a[1]).map(([user, count]) => (
                <div key={user} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-xs">
                      {user.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{user}</span>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-white dark:bg-gray-700 text-xs font-bold shadow-sm border border-gray-100 dark:border-gray-600">
                    {count}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, color, description, highlight = false }) {
  const colors = {
    indigo: "from-indigo-500 to-indigo-600 text-indigo-500 bg-indigo-50 dark:bg-indigo-950/40",
    green: "from-green-500 to-green-600 text-green-500 bg-green-50 dark:bg-green-950/40",
    red: "from-red-500 to-red-600 text-red-500 bg-red-50 dark:bg-red-950/40",
    amber: "from-amber-500 to-amber-600 text-amber-500 bg-amber-50 dark:bg-amber-950/40",
    fuchsia: "from-fuchsia-500 to-fuchsia-600 text-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/40",
    blue: "from-blue-500 to-blue-600 text-blue-500 bg-blue-50 dark:bg-blue-950/40",
  };

  return (
    <Card className={`border-none shadow-sm bg-white dark:bg-gray-900 overflow-hidden relative ${highlight ? 'ring-1 ring-red-100 dark:ring-red-900/50 shadow-md' : ''}`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</p>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{value}</h3>
          </div>
          <div className={`p-2.5 rounded-xl ${colors[color].split(' ')[1]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        <p className="mt-4 text-[11px] text-gray-500 dark:text-gray-400 font-medium">
          {description}
        </p>
        
        {/* Glow effect for high priority */}
        {highlight && (
          <div className="absolute top-0 right-0 w-16 h-16 -mr-8 -mt-8 bg-red-500/10 blur-2xl rounded-full" />
        )}
      </CardContent>
    </Card>
  );
}
